import { useEffect, useState } from "react";
import { Dropdown, Page, PageHeader } from "@iq/models";
import Filter from "../Components/Filter/Filter"
import KanbanBoard from "../Components/Kanban/KanbanBoard";
import CreateProjectModal from "../Components/modals/CreateProjectModal";
import ViewProjectModal from "../Components/modals/ViewProjectModal";
import {
	listProjectTasks,
	updateTask,
} from "../Services/pm_service";
import "../Styles/Kanban.css";


function KanbanPage({setPageHeader, selectedProject, setSelectedProject}) {
	const selectedProjectId = selectedProject?.id ?? null;
	const [projectsLoading, setProjectsLoading] = useState(false);
	const [tasks, setTasks] = useState([]);
	const [tasksLoading, setTasksLoading] = useState(false);
	const [createProjectOpen, setCreateProjectOpen] = useState(false);
	const [viewProjectOpen, setViewProjectOpen] = useState(false);
	const [filteredTasks, setFilteredTasks] = useState([]);

	useEffect(() => {
            if (!selectedProjectId) {
                setTasks([]);
                setTasksLoading(false);
                return;
            }

            let cancelled = false;

            async function loadTasks() {
                setTasksLoading(true);
                try {
                    const data = await listProjectTasks(selectedProjectId);
                    if (!cancelled) {
                        setTasks(Array.isArray(data) ? data : []);
                    }
                } catch (error) {
                    console.error("Failed to load PM tasks", error);
                    if (!cancelled) {
                        setTasks([]);
                    }
                } finally {
                    if (!cancelled) {
                        setTasksLoading(false);
                    }
                }
            }

            loadTasks();
            return () => {
                cancelled = true;
            };
        }, [selectedProjectId]);



	async function refreshTasksForSelectedProject() {
		if (!selectedProjectId) {
			setTasks([]);
			return;
		}

		try {
			const data = await listProjectTasks(selectedProjectId);
			setTasks(Array.isArray(data) ? data : []);
		} catch (error) {
			console.error("Failed to refresh PM tasks", error);
		}
	}

	async function handleMove(move) {
		if (!move || move.fromColumnId === move.toColumnId) {
			return;
		}

		const taskId = Number(move.itemId);
		if (!Number.isFinite(taskId)) {
			throw new Error("Invalid task id in PM kanban move payload");
		}

		await updateTask(taskId, {
			status: move.toColumnId,
			sortOrder: move.destinationIndex,
		});
	}

	useEffect(() => {
		setPageHeader?.({
			title: "Kanban",
			back: false,
			onBack: null,
			children: (
				<Filter
				tasks={tasks}
				selectedProject={selectedProject}
				onFilteredTasksChange={setFilteredTasks}
				onProjectChange= {(project) => {
					setSelectedProject(project);
				}}
				tasksLoading={tasksLoading}
				projectsLoading={projectsLoading}
				onCreateProject = {() => setCreateProjectOpen(true)}
				onEditProject={() => setViewProjectOpen(true)}/>
			),
		});
	}, [setPageHeader, tasks, tasksLoading]);

	return (
		<Page>
			<KanbanBoard
				projectId={selectedProjectId}
				tasks={filteredTasks}
				onMove={handleMove}
				onTaskCreated={async () => {
					await refreshTasksForSelectedProject();
				}}
				onTaskUpdated={async () => {
					await refreshTasksForSelectedProject();
				}}
			/>

			<CreateProjectModal
				isOpen={createProjectOpen}
				onClose={() => setCreateProjectOpen(false)}
				onCreated={async (createdProject) => {
					setSelectedProject(createdProject ?? null);
				}}
			/>

			<ViewProjectModal
				isOpen={viewProjectOpen && Boolean(selectedProject)}
				onClose={() => setViewProjectOpen(false)}
				project={selectedProject}
				tasks={tasks}
				onSaved={async () => {
					await refreshTasksForSelectedProject();
				}}
				onDeleted={async () => {
					await refreshTasksForSelectedProject();
				}}
			/>
		</Page>
	);
}

export default KanbanPage;
